import React, { useState, forwardRef, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
    Plus,
    Settings,
    Trash2,
    Sun,
    Moon,
    LogOut,
    Keyboard,
    ChevronLeft,
    Menu,
    X,
    Search,
} from 'lucide-react';
import { signOutUser } from '../../firebase/firebase';
import SessionsList from './SessionsList/SessionsList';
import SearchBarModal from '../SearchBarModal/SearchBarModal';
import Button from '../Button/Button';

interface ThemeColors {
  primary?: string;
  textPrimary?: string;
  textSecondary?: string;
  backgroundPrimary?: string;
  backgroundSecondary?: string;
  backgroundTertiary?: string;
  borderColor?: string;
  [key: string]: unknown;
}

interface SidebarComponentProps {
  theme: ThemeColors;
  isSidebarCollapsed: boolean;
  setIsSidebarCollapsed: (v: boolean) => void;
  isMobile: boolean;
  user?: { email?: string; name?: string; picture?: string };
  loadSession: (session: unknown) => void;
  createNewChat: (agentType?: string) => void;
  clearAllHistory: () => void;
  isDarkMode: boolean;
  toggleTheme: () => void;
  showSessions: boolean;
  setShowSessions: (v: boolean) => void;
}

const SidebarComponent = forwardRef<{ loadSessions: () => Promise<unknown> }, SidebarComponentProps>(({
    theme,
    isSidebarCollapsed,
    setIsSidebarCollapsed,
    isMobile,
    user,
    loadSession,
    createNewChat,
    clearAllHistory,
    isDarkMode,
    toggleTheme,
    showSessions,
    setShowSessions,
}, ref) => {
    void setShowSessions;
    const [showProfileOptions, setShowProfileOptions] = useState(false);
    const [showAgentModal, setShowAgentModal] = useState(false);
    const [showShortcutsModal, setShowShortcutsModal] = useState(false);
    const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
    const profileMenuRef = useRef<HTMLDivElement>(null);
    const agentModalRef = useRef<HTMLDivElement>(null);
    const primaryShortcutKeys = ['a', 's', 'd', 'f'];
    const modalShortcutKeys = ['a', 's', 'd', 'w', 'f'];

    const isEditableTarget = (target: EventTarget | null): boolean => {
        const element = target as HTMLElement | null;
        if (!element) return false;
        const tagName = element.tagName.toLowerCase();
        return (
            element.isContentEditable ||
            tagName === 'input' ||
            tagName === 'textarea' ||
            tagName === 'select' ||
            Boolean(element.closest('input, textarea, select, [contenteditable="true"]'))
        );
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
                setShowProfileOptions(false);
            }
            if (agentModalRef.current && !agentModalRef.current.contains(event.target as Node)) {
                setShowAgentModal(false);
            }
        };

        if (showProfileOptions || showAgentModal) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showProfileOptions, showAgentModal]);

    const handleLogout = (): void => {
        localStorage.clear();
        signOutUser();
    };

    const availableAgents = [
        // { id: 'general', name: 'General Assistant', description: 'General purpose AI assistant' },
        // { id: 'FollowUpAgent', name: 'Follow-Up Agent', description: 'Manages follow-up events and reminders' },
        { id: 'famfincal_agent', name: 'Family Finance Agent', description: 'Helps with family finance planning' },
        { id: 'CreateEventAgent', name: 'Crud Supervisor Calendar Agent', description: 'Helps manage events' },
        {
            id: 'ReceiveMessageAgent',
            name: 'Receive Message Agent',
            description:
                'Runs CRM receive-message flow (Sheets → schedule → Telnyx). Frontend sends thread_id, user_id, and email_agent_prompt to /receive_message_agent.',
        },
        {
            id: 'CRMSendMessageAgent',
            name: 'CRM Send Message Agent',
            description:
                'POST /crm_send_message_agent — SMS via Telnyx. Set session.crm_to / crm_from or crm_send_message { to, text, from }.',
        },
        {
            id: 'PlanMessageAgent',
            name: 'Plan Message Agent',
            description:
                'Runs weekly CRM planner flow via POST /plan_message_agent.',
        },
        {
            id: 'CustomerProfileAgent',
            name: 'Customer Profile Agent',
            description:
                'Syncs CRM contacts and prompts for missing profile fields via POST /customer_profile_endpoint.',
        },
    ];
    const keyboardShortcutSections = [
        {
            title: 'Global (when not typing)',
            items: [
                { keys: 'A', action: 'Create new chat' },
                { keys: 'S', action: 'Open agent selector' },
                { keys: 'D', action: 'Open/close profile options' },
                { keys: 'F', action: 'Open search modal' },
                { keys: 'Esc', action: 'Close open dropdowns/modals' },
            ],
        },
        {
            title: 'Profile Options Menu',
            items: [
                { keys: 'W', action: 'Open keyboard shortcuts modal' },
                { keys: 'A', action: 'Settings (current placeholder action)' },
                { keys: 'S', action: 'Clear history' },
                { keys: 'D', action: 'Toggle dark/light mode' },
                { keys: 'F', action: 'Logout' },
            ],
        },
        {
            title: 'Agent Selector Modal',
            items: [
                { keys: 'A', action: 'Select Family Finance Agent' },
                { keys: 'S', action: 'Select Crud Supervisor Calendar Agent' },
                { keys: 'D', action: 'Select CRM Outreach Agent' },
                { keys: 'Esc', action: 'Close modal' },
            ],
        },
        {
            title: 'Search Modal',
            items: [
                { keys: 'Space', action: 'Focus search input' },
                { keys: 'N', action: 'Open create document form' },
                { keys: 'Enter', action: 'Execute search (in search input)' },
                { keys: 'Esc', action: 'Close form popup or search modal' },
            ],
        },
        {
            title: 'Chat Input',
            items: [
                { keys: 'Space', action: 'Focus message input (outside editable fields)' },
                { keys: 'Enter', action: 'Send message' },
                { keys: 'Shift + Enter', action: 'Insert a new line in message input' },
            ],
        },
    ];

    useEffect(() => {
        const profileActions: Array<() => void> = [
            () => setShowProfileOptions(false), // Settings (placeholder action in current UI)
            clearAllHistory,
            toggleTheme,
            () => setShowShortcutsModal(true),
            handleLogout,
        ];

        const handleGlobalShortcuts = (event: KeyboardEvent) => {
            if (event.defaultPrevented) {
                return;
            }
            const key = event.key.toLowerCase();

            if (key === 'escape') {
                if (showShortcutsModal) {
                    setShowShortcutsModal(false);
                    event.preventDefault();
                    return;
                }
                if (showAgentModal) {
                    setShowAgentModal(false);
                    event.preventDefault();
                    return;
                }
                if (showProfileOptions) {
                    setShowProfileOptions(false);
                    event.preventDefault();
                    return;
                }
                return;
            }

            if (showShortcutsModal) {
                return;
            }

            if (isEditableTarget(event.target)) {
                return;
            }

            if (showAgentModal) {
                const index = modalShortcutKeys.indexOf(key);
                if (index >= 0 && index < availableAgents.length) {
                    handleAgentSelect(availableAgents[index].id);
                    event.preventDefault();
                }
                return;
            }

            if (showProfileOptions) {
                const index = modalShortcutKeys.indexOf(key);
                if (index >= 0 && index < profileActions.length) {
                    profileActions[index]();
                    setShowProfileOptions(false);
                    event.preventDefault();
                }
                return;
            }

            if (isSearchModalOpen) {
                return;
            }

            if (key === primaryShortcutKeys[0]) {
                createNewChat();
                event.preventDefault();
            } else if (key === primaryShortcutKeys[1]) {
                setShowAgentModal(true);
                event.preventDefault();
            } else if (key === primaryShortcutKeys[2]) {
                setShowProfileOptions((prev) => !prev);
                event.preventDefault();
            } else if (key === primaryShortcutKeys[3]) {
                setIsSearchModalOpen(true);
                event.preventDefault();
            }
        };

        document.addEventListener('keydown', handleGlobalShortcuts);
        return () => {
            document.removeEventListener('keydown', handleGlobalShortcuts);
        };
    }, [
        clearAllHistory,
        createNewChat,
        isSearchModalOpen,
        showAgentModal,
        showProfileOptions,
        showShortcutsModal,
        toggleTheme,
    ]);

    const handleAgentSelect = (agentId: string): void => {
        createNewChat(agentId);
        setShowAgentModal(false);
    };

    return (
        <>
            <div
            className={`${
                isSidebarCollapsed ? 'w-0' : 'w-64'
            } transition-all duration-300 flex flex-col ${
                isMobile && !isSidebarCollapsed
                    ? 'absolute inset-y-0 left-0 z-40'
                    : ''
            }`}
            style={{
                backgroundColor: theme.backgroundTertiary,
                display: isSidebarCollapsed ? 'none' : 'flex',
            }}
        >
            <div className='p-5 h-full flex flex-col'>
                <div className='flex items-center justify-between mb-6'>
                    <h1
                        className='text-xl font-bold'
                        style={{ color: theme.primary }}
                    >
                        SDS
                    </h1>
                    {isMobile && (
                        <Button
                            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                            className='p-1'
                        >
                            <ChevronLeft size={18} color={theme.textPrimary} />
                        </Button>
                    )}
                </div>

                <div className='flex gap-3 mb-3'>
                    <Button
                        onClick={() => createNewChat()}
                        className='flex-1 p-3'
                        title='New Chat (a)'
                        style={{
                            backgroundColor: theme?.primary || '#7c4dff',
                            color: '#fff',
                        }}
                    >
                        <Plus size={16} className='mr-2' />
                        New Chat
                    </Button>

                    <Button
                        onClick={() => setShowAgentModal(true)}
                        className='flex-1 p-3 border'
                        title='Select Agent (s)'
                        style={{ 
                            backgroundColor: theme.backgroundPrimary,
                            borderColor: theme.borderColor,
                            color: theme.textPrimary
                        }}
                    >
                        <Menu size={16} className='mr-2' />
                        Callable Agents
                    </Button>
                </div>

                <div className='flex-1 overflow-y-auto mb-4'>
                    <h3
                        className='text-sm mb-2 px-1'
                        style={{ color: theme.textSecondary }}
                    >
                        Chat History
                    </h3>
                    
                    {showSessions && (
                        <div className='mb-4'>
                            <SessionsList
                                ref={ref}
                                userEmail={user?.email}
                                onSessionClick={loadSession}
                                theme={theme}
                            />
                        </div>
                    )}
                </div>

                <div
                    ref={profileMenuRef}
                    className='flex gap-3 mb-3 relative'
                >
                    <Button
                        onClick={() => setShowProfileOptions(!showProfileOptions)}
                        className='flex-1 flex items-center p-2 rounded-lg transition-colors min-w-0 overflow-hidden z-10 hover:bg-gray-100'
                        title='Profile Options (d)'
                        style={{ 
                            backgroundColor: theme.backgroundPrimary,
                            border: `1px solid ${theme.borderColor}`,
                            color: theme.textPrimary
                        }}
                        aria-label='User Profile Options'
                    >
                        <img
                            src={user?.picture}
                            alt={user?.name || 'User avatar'}
                            className='w-6 h-6 rounded-full mr-2 flex-shrink-0'
                        />
                        <div className='flex flex-col text-left min-w-0 flex-1'>
                            <span
                                className='text-xs font-medium truncate leading-tight'
                                style={{ color: theme.textPrimary }}
                            >
                                {user?.name}
                            </span>
                            <span
                                className='text-xs truncate leading-tight'
                                style={{ color: theme.textSecondary }}
                            >
                                {user?.email?.slice(0, 15)}
                            </span>
                        </div>
                    </Button>

                    <Button
                        onClick={() => setIsSearchModalOpen(true)}
                        className='flex-1 text-sm whitespace-nowrap min-w-0 z-10'
                        title='Search (f)'
                        style={{
                            backgroundColor: theme?.primary || '#7c4dff',
                            color: '#fff',
                        }}
                    >
                        <Search size={16} className='mr-1.5 flex-shrink-0' />
                        <span className='truncate'>Search</span>
                    </Button>

                    {showProfileOptions && (
                        <div
                            className='absolute bottom-full left-0 right-0 mx-2 mb-2 rounded-lg shadow-lg p-2 z-20'
                            style={{
                                backgroundColor: theme.backgroundPrimary,
                                border: `1px solid ${theme.borderColor}`,
                                width: 'calc(100% - 32px)',
                            }}
                        >
                            <Button
                                onClick={() => {
                                    setShowShortcutsModal(true);
                                    setShowProfileOptions(false);
                                }}
                                className='w-full flex items-center !justify-start text-left p-2 hover:bg-gray-100 rounded text-sm'
                                title='Keyboard Shortcuts (w)'
                            >
                                <Keyboard
                                    size={14}
                                    className='mr-2'
                                    color={theme.textSecondary}
                                />
                                <span style={{ color: theme.textSecondary }}>
                                    Keyboard Shortcuts
                                </span>
                            </Button>

                            <Button
                                className='w-full flex items-center !justify-start text-left p-2 hover:bg-gray-100 rounded text-sm'
                                onClick={() => setShowProfileOptions(false)}
                                title='Settings (a)'
                            >
                                <Settings
                                    size={14}
                                    className='mr-2'
                                    color={theme.primary}
                                />
                                <span style={{ color: theme.textPrimary }}>Settings</span>
                            </Button>

                            <Button
                                onClick={clearAllHistory}
                                className='w-full flex items-center !justify-start text-left p-2 hover:bg-gray-100 rounded text-sm'
                                title='Clear History (s)'
                            >
                                <Trash2
                                    size={14}
                                    className='mr-2'
                                    color={theme.textSecondary}
                                />
                                <span style={{ color: theme.textSecondary }}>
                                    Clear History
                                </span>
                            </Button>

                            <Button
                                onClick={toggleTheme}
                                className='w-full flex items-center !justify-start text-left p-2 hover:bg-gray-100 rounded text-sm'
                                title={`${isDarkMode ? 'Light Mode' : 'Dark Mode'} (d)`}
                            >
                                {isDarkMode ? (
                                    <Sun
                                        size={14}
                                        className='mr-2'
                                        color={theme.textSecondary}
                                    />
                                ) : (
                                    <Moon
                                        size={14}
                                        className='mr-2'
                                        color={theme.textSecondary}
                                    />
                                )}
                                <span style={{ color: theme.textSecondary }}>
                                    {isDarkMode ? 'Light Mode' : 'Dark Mode'}
                                </span>
                            </Button>

                            <div
                                className='h-px my-1'
                                style={{ backgroundColor: theme.borderColor }}
                            />

                            <Button
                                onClick={handleLogout}
                                className='w-full flex items-center !justify-start text-left p-2 hover:bg-gray-100 rounded text-sm'
                                title='Logout (f)'
                            >
                                <LogOut size={14} className='mr-2' color='#ff4444' />
                                <span className='text-red-500'>Logout</span>
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            {showAgentModal && createPortal(
                <div 
                    className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'
                    onClick={() => setShowAgentModal(false)}
                >
                    <div
                        ref={agentModalRef}
                        className='p-6 rounded-lg w-96 max-w-[90vw] max-h-[80vh] overflow-y-auto'
                        style={{ 
                            backgroundColor: theme.backgroundPrimary,
                            border: `1px solid ${theme.borderColor}`
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className='flex items-center justify-between mb-4'>
                            <h3
                                className='text-lg font-semibold'
                                style={{ color: theme.textPrimary }}
                            >
                                Select an Agent
                            </h3>
                            <Button
                                onClick={() => setShowAgentModal(false)}
                                className='p-1 rounded hover:bg-gray-100'
                                style={{ color: theme.textSecondary }}
                            >
                                <X size={20} />
                            </Button>
                        </div>
                        
                        <div className='space-y-2'>
                            {availableAgents.map((agent, index) => {
                                const shortcut = modalShortcutKeys[index] || '';
                                return (
                                <Button
                                    key={agent.id}
                                    onClick={() => handleAgentSelect(agent.id)}
                                    className='w-full flex-col items-start justify-start text-left p-4 rounded-lg border transition-colors hover:bg-opacity-50'
                                    title={`${agent.name} (${shortcut})`}
                                    style={{
                                        backgroundColor: theme.backgroundTertiary,
                                        borderColor: theme.borderColor,
                                        color: theme.textPrimary,
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = theme.backgroundSecondary || '';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = theme.backgroundTertiary || '';
                                    }}
                                >
                                    <div className='text-lg font-bold mb-1' style={{ color: theme.textPrimary }}>
                                        {agent.name}
                                    </div>
                                    <div className='text-sm' style={{ color: theme.textSecondary }}>
                                        {agent.description}
                                    </div>
                                </Button>
                                );
                            })}
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {showShortcutsModal && createPortal(
                <div
                    className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'
                    onClick={() => setShowShortcutsModal(false)}
                >
                    <div
                        className='p-6 rounded-lg w-[40rem] max-w-[92vw] max-h-[82vh] overflow-y-auto'
                        style={{
                            backgroundColor: theme.backgroundPrimary,
                            border: `1px solid ${theme.borderColor}`,
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className='flex items-center justify-between mb-4'>
                            <h3
                                className='text-lg font-semibold'
                                style={{ color: theme.textPrimary }}
                            >
                                Keyboard Shortcuts
                            </h3>
                            <Button
                                onClick={() => setShowShortcutsModal(false)}
                                className='p-1 rounded hover:bg-gray-100'
                                style={{ color: theme.textSecondary }}
                            >
                                <X size={20} />
                            </Button>
                        </div>

                        <div className='space-y-4'>
                            {keyboardShortcutSections.map((section) => (
                                <div
                                    key={section.title}
                                    className='rounded-lg border p-3'
                                    style={{
                                        borderColor: theme.borderColor,
                                        backgroundColor: theme.backgroundTertiary,
                                    }}
                                >
                                    <h4
                                        className='text-sm font-semibold mb-2'
                                        style={{ color: theme.textPrimary }}
                                    >
                                        {section.title}
                                    </h4>
                                    <div className='space-y-1.5'>
                                        {section.items.map((item) => (
                                            <div
                                                key={`${section.title}-${item.keys}-${item.action}`}
                                                className='flex items-center justify-between gap-3 text-sm'
                                            >
                                                <span style={{ color: theme.textSecondary }}>
                                                    {item.action}
                                                </span>
                                                <span
                                                    className='px-2 py-0.5 rounded border text-xs font-semibold whitespace-nowrap'
                                                    style={{
                                                        color: theme.textPrimary,
                                                        borderColor: theme.borderColor,
                                                        backgroundColor: theme.backgroundPrimary,
                                                    }}
                                                >
                                                    {item.keys}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>

            <SearchBarModal
                isOpen={isSearchModalOpen}
                onClose={() => setIsSearchModalOpen(false)}
                theme={theme}
            />
        </>
    );
});

SidebarComponent.displayName = 'SidebarComponent';

export default SidebarComponent;
